import {useCallback, useEffect, useState} from 'react';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';

import packageJson from '@/package.json';

import {useCountriesInfoContext} from '@/contexts/CountriesInfoContext';
import {useAuthenticationServices} from '@/hooks/login/useAuthenticationServices';
import {useEvent} from '@/hooks/useTracking';
import {EmailAuthenticationService} from '@/services/auth/EmailAuthenticationService';
import {mixPanel} from '@/services/tracking/analytics/MixPanel';
import {tracking} from '@/services/tracking/TrackingService';
import {isIOS, isPwaInstalled} from '@/utils/device';
import {createResendCooldownDate} from '@/utils/time';

// DO NOT IMPORT DYNAMICALLY!
// This will cause the Email Form to reset its state whenever an error occurs
import {EnterEmailForm} from '@/components/auth/forms/EnterEmailForm/EnterEmailForm';

import s from './EnterEmailSignup.module.scss';
import { useLocation } from '@/contexts/LocationContext';
import AuthWithGoogle from '../../AuthWithGoogle/AuthWithGoogle';
import { Typography } from '@kasta-io/components';
import { useIsNativeWrapper } from '@/contexts/UserContext';

export const EnterEmailSignup = () => {
  const router = useRouter();
  const {emailService, userService} = useAuthenticationServices();
  const {countriesInfo} = useCountriesInfoContext();
  const { countryCode, ip } = useLocation();
  const isNativeWrapper = useIsNativeWrapper();

  const [error, setError] = useState<unknown | null>(null);
  const [fallbackErrorMsg, setFallbackErrorMsg] = useState<string>();

  const [acceptedMarketingOptIn, setAcceptedMarketingOptIn] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(true);

  const SignInSignUpToggle = dynamic(
    () =>
      import('@/components/auth/SignInSignUpToggle/SignInSignUpToggle').then(mod => mod.SignInSignUpToggle),
    {ssr: false},
  );

  const TermsAndConditions = dynamic(
    () => import('@/components/auth/signup/EnterEmailSignup/TermsAndConditionsCaption'),
    {ssr: false},
  );

  const handleAcceptTerms = () => {
    setAcceptedTerms(!acceptedTerms);
  };

  const handleAcceptedExclusiveOffers = () => {
    setAcceptedMarketingOptIn(!acceptedMarketingOptIn);

    if (!acceptedMarketingOptIn) {
      tracking.logEvent(tracking.events.user_wants_marketing_offers_signup());
    }
  };

  useEvent({
    name: 'signup',
    params: {status: 'second screen | enter email'},
    idempotencyId: 'signup_enter_email',
  });

  useEffect(() => {
    const distinctId = mixPanel?.getDistinctId();

    if (distinctId) {
      mixPanel?.identify(distinctId);

      const {name, region, subregion} = countriesInfo[countryCode] ?? {
        name: {common: ''},
        region: '',
        subregion: '',
      };

      mixPanel?.set({
        countryCode,
        countryName: name.common,
        region,
        subregion,
        'PWA Installed': isPwaInstalled,
        'PWA Version': packageJson.version,
      });

      mixPanel?.setUTMProperties();

      mixPanel?.register({
        pwa_installed: isPwaInstalled,
        pwa_version: packageJson.version,
      });

      mixPanel?.set({ip: ip});
    }
  }, [countriesInfo, countryCode, ip, userService]);

  const navigateToEmailSentPage = useCallback(
    (emailProp?: string) => {
      const email = emailProp || emailService.getIdentifier() || '';
      const resendCooldownEnd = createResendCooldownDate(emailService.COOLDOWN_TIME).toISOString();
      const params = new URLSearchParams({email, resendCooldownEnd, countryCode}).toString();
      router.push(`/signup/email-sent?${params}`);
    },
    [countryCode, emailService, router],
  );

  const onIsDirty = useCallback(() => {
    tracking.logEvent(tracking.events.start_filling_email_signup());
  }, []);

  const handleSubmitEmail = useCallback(
    async ({email}: {email: string}) => {
      try {
        tracking.logEvent(tracking.events.submit_email_signup(email));
        await emailService.validateIdentifier({
          identifier: email,
          throwOn: EmailAuthenticationService.THROW_ON.USER_ALREADY_EXISTS,
        });
      } catch (error: unknown) {
        setError(error);
        setFallbackErrorMsg(EmailAuthenticationService.GENERIC_ERROR.VALIDATE_EMAIL);
        return;
      }

      try {
        // We call this one first because we need an AUTH# record to send an email
        await userService.initiateSignup({email, countryCode, marketingOptIn: acceptedMarketingOptIn});
      } catch (error: unknown) {
        setError(error);
        setFallbackErrorMsg(EmailAuthenticationService.GENERIC_ERROR.INITIATE_SIGNUP);
        return;
      }

      try {
        tracking.logEvent(tracking.events.send_email_code_signup());
        await emailService.sendVerificationMechanism({identifier: email});
      } catch (error: unknown) {
        setError(error);
        setFallbackErrorMsg(EmailAuthenticationService.GENERIC_ERROR.SEND_EMAIL);
        return;
      }

      navigateToEmailSentPage(email);
      emailService.saveIdentifier(email);
    },
    [acceptedMarketingOptIn, countryCode, emailService, navigateToEmailSentPage, userService],
  );

  const handleUserAlreadyExists = useCallback(
    (email: string) => {
      const data = {countryCode, email};
      const params = new URLSearchParams(data).toString();
      return router.push(`/signup/duplicate-account?${params}`);
    },
    [countryCode, router],
  );

  const handleResumeSignup = useCallback(
    async (email: string) => {
      try {
        await emailService.sendVerificationMechanism({identifier: email});
      } catch (error: unknown) {
        setError(error);
        setFallbackErrorMsg(EmailAuthenticationService.GENERIC_ERROR.SEND_EMAIL);
        return;
      }

      navigateToEmailSentPage(email);
    },
    [emailService, navigateToEmailSentPage],
  );

  const onLoginClick = useCallback(() => {
    router.push(`/login`);
  }, [router]);

  return (
    <main className={s.main}>
      <div className={s.content}>
        <h1>Sign up</h1>
        <EnterEmailForm
          label="Sign up"
          acceptedTerms={acceptedTerms}
          flow="signup"
          onUseCodeSent={navigateToEmailSentPage}
          Prefooter={() => (
            <TermsAndConditions
              handleAcceptTerms={handleAcceptTerms}
              acceptedTerms={acceptedTerms}
              handleMarketingOptIn={handleAcceptedExclusiveOffers}
              acceptedMarketingOptIn={acceptedMarketingOptIn}
            />
          )}
          Postfooter={() => (
            <>
              <SignInSignUpToggle goTo="signin" onClick={onLoginClick} />
              {isNativeWrapper && isIOS() 
                ? null 
                : <div className={s.signupWithGoogle}>
                    <Typography 
                      variant="caption1" 
                      color='--colors-text-onBackgroundLight'> Or sign up with </Typography>
                    <AuthWithGoogle mode='signUp' />
                  </div>
              }
            </>
          )}
          error={error}
          fallbackErrorMsg={fallbackErrorMsg}
          onSubmitEmail={handleSubmitEmail}
          onUserAlreadyExists={handleUserAlreadyExists}
          onUserStatusError={handleResumeSignup}
          onIsDirty={onIsDirty}
        />
      </div>
    </main>
  );
};
